import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  FiSave, 
  FiArrowLeft, 
  FiCalendar, 
  FiClock, 
  FiMapPin, 
  FiUser, 
  FiUsers,
  FiPlus,
  FiX,
  FiSearch
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './VisitaForm.css';

const VisitaForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(false);
  const [loadingMembros, setLoadingMembros] = useState(false);
  const [membrosDisponiveis, setMembrosDisponiveis] = useState([]);
  const [buscarMembro, setBuscarMembro] = useState('');
  const [showMembrosModal, setShowMembrosModal] = useState(false);
  const [localBusca, setLocalBusca] = useState('');
  const [showLocalSearch, setShowLocalSearch] = useState(false);
  const localBuscaRef = useRef(null);
  
  const [formData, setFormData] = useState({
    titulo: '',
    tipo_visita: '',
    data_visita: '',
    hora_inicio: '',
    hora_fim: '',
    local_visita: '',
    endereco: '',
    responsavel_id: '',
    observacoes: '',
    status: 'planejada'
  });
  
  const [participantes, setParticipantes] = useState([]);
  
  const tiposVisita = [
    { value: 'mocidade', label: 'Visita - Mocidade' },
    { value: 'criancas', label: 'Visita - Crianças' },
    { value: 'ajuntamento', label: 'Ajuntamento' },
    { value: 'reuniao_familiar', label: 'Reunião Familiar' },
    { value: 'reuniao_evangelizacao', label: 'Reunião Evangelização' }
  ];
  
  const statusOptions = [
    { value: 'planejada', label: 'Planejada' },
    { value: 'realizada', label: 'Realizada' },
    { value: 'cancelada', label: 'Cancelada' }
  ];

  const carregarMembros = async () => {
    try {
      setLoadingMembros(true);
      const response = await api.get('/irmandade/todos');
      setMembrosDisponiveis(response.data);
    } catch (error) {
      console.error('Erro ao carregar membros:', error);
      toast.error('Erro ao carregar membros');
    } finally {
      setLoadingMembros(false);
    }
  };

  const carregarVisita = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/visitas/${id}`);
      const visita = response.data;
      
      setFormData({
        titulo: visita.titulo || '',
        tipo_visita: visita.tipo_visita || '',
        data_visita: visita.data_visita ? visita.data_visita.split('T')[0] : '',
        hora_inicio: visita.hora_inicio || '',
        hora_fim: visita.hora_fim || '',
        local_visita: visita.local_visita || '',
        endereco: visita.endereco || '',
        responsavel_id: visita.responsavel_id || '',
        observacoes: visita.observacoes || '',
        status: visita.status || 'planejada'
      });
      
      // Carregar participantes
      const participantesResponse = await api.get(`/visitas/${id}/participantes`);
      setParticipantes(participantesResponse.data);
      
    } catch (error) {
      console.error('Erro ao carregar visita:', error);
      toast.error('Erro ao carregar dados da visita');
      navigate('/visitas');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // UseEffect DEPOIS das definições de função
  useEffect(() => {
    carregarMembros();
    if (isEditing) {
      carregarVisita();
    }
  }, [isEditing, carregarVisita]);

  // Fechar dropdown local ao clicar fora
  useEffect(() => {
    function handleClickOutside(e) {
      if (localBuscaRef.current && !localBuscaRef.current.contains(e.target)) {
        setShowLocalSearch(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (campo, valor) => {
    setFormData(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const adicionarParticipante = (membro) => {
    if (!participantes.find(p => p.id === membro.id)) {
      setParticipantes(prev => [...prev, {
        ...membro,
        presente: false
      }]);
    }
    setShowMembrosModal(false);
    setBuscarMembro('');
  };

  const removerParticipante = (membroId) => {
    setParticipantes(prev => prev.filter(p => p.id !== membroId));
  };

  const togglePresenca = (membroId) => {
    setParticipantes(prev => prev.map(p => 
      p.id === membroId ? { ...p, presente: !p.presente } : p
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.titulo || !formData.tipo_visita || !formData.data_visita) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    try {
      setLoading(true);
      
      const visitaData = {
        ...formData,
        responsavel_id: formData.responsavel_id || null
      };
      
      let visitaId;
      
      if (isEditing) {
        await api.put(`/visitas/${id}`, visitaData);
        visitaId = id;
        toast.success('Visita atualizada com sucesso!');
      } else {
        const response = await api.post('/visitas', visitaData);
        visitaId = response.data.id;
        toast.success('Visita criada com sucesso!');
      }
      
      // Atualizar participantes
      if (participantes.length > 0) {
        const participantesData = participantes.map(p => ({
          membro_id: p.id,
          presente: p.presente || false
        }));
        
        await api.post(`/visitas/${visitaId}/participantes`, {
          participantes: participantesData
        });
      }
      
      navigate('/visitas');
      
    } catch (error) {
      console.error('Erro ao salvar visita:', error);
      toast.error('Erro ao salvar visita');
    } finally {
      setLoading(false);
    }
  };

  const getMemberName = (m) => m.nome_completo || m.nomeCompleto || m.nome || '';

  const membrosFiltered = membrosDisponiveis.filter(membro => {
    const nome = getMemberName(membro);
    const categoria = membro.categoria || '';
    const termo = buscarMembro.toLowerCase();
    return nome.toLowerCase().includes(termo) || categoria.toLowerCase().includes(termo);
  });

  // Filtro de membros para o campo Local baseado no tipo de visita
  const membrosParaLocal = membrosDisponiveis.filter(m => {
    const cat = m.categoria || '';
    if (formData.tipo_visita === 'mocidade') return cat === 'jovem';
    if (formData.tipo_visita === 'criancas') return cat === 'crianca';
    return true;
  });

  const membrosLocalFiltrados = membrosParaLocal.filter(m =>
    getMemberName(m).toLowerCase().includes(localBusca.toLowerCase())
  );

  const selecionarMembroLocal = (membro) => {
    const nome = getMemberName(membro);
    const endereco = membro.endereco || '';
    handleInputChange('local_visita', nome);
    handleInputChange('endereco', endereco);
    setLocalBusca('');
    setShowLocalSearch(false);
  };

  if (loading && isEditing) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando dados da visita...</p>
      </div>
    );
  }

  return (
    <div className="visita-form-container">
      <div className="form-header">
        <button 
          onClick={() => navigate('/visitas')} 
          className="btn btn-secondary"
        >
          <FiArrowLeft /> Voltar
        </button>
        <h1>{isEditing ? 'Editar Visita' : 'Nova Visita'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="visita-form">
        <div className="form-section">
          <h2>Informações Básicas</h2>
          
          <div className="form-grid">
            <div className="form-group">
              <label>Título *</label>
              <input
                type="text"
                value={formData.titulo}
                onChange={(e) => handleInputChange('titulo', e.target.value)}
                placeholder="Digite o título da visita"
                required
              />
            </div>
            
            <div className="form-group">
              <label>Tipo de Visita *</label>
              <select
                value={formData.tipo_visita}
                onChange={(e) => handleInputChange('tipo_visita', e.target.value)}
                required
              >
                <option value="">Selecione o tipo</option>
                {tiposVisita.map(tipo => (
                  <option key={tipo.value} value={tipo.value}>
                    {tipo.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Data e Horário</h2>
          
          <div className="form-grid">
            <div className="form-group">
              <label>Data da Visita *</label>
              <div className="input-with-icon">
                <FiCalendar />
                <input
                  type="date"
                  value={formData.data_visita}
                  onChange={(e) => handleInputChange('data_visita', e.target.value)}
                  required
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Hora de Início</label>
              <div className="input-with-icon">
                <FiClock />
                <input
                  type="time"
                  value={formData.hora_inicio}
                  onChange={(e) => handleInputChange('hora_inicio', e.target.value)}
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Hora de Término</label>
              <div className="input-with-icon">
                <FiClock />
                <input
                  type="time"
                  value={formData.hora_fim}
                  onChange={(e) => handleInputChange('hora_fim', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Local</h2>
          
          <div className="form-grid">
            {/* Busca por membro cadastrado */}
            <div className="form-group full-width">
              <label>
                Buscar membro cadastrado
                {formData.tipo_visita === 'mocidade' && <span className="local-busca-hint"> — mostrando jovens</span>}
                {formData.tipo_visita === 'criancas' && <span className="local-busca-hint"> — mostrando crianças</span>}
                {!formData.tipo_visita && <span className="local-busca-hint"> — selecione um tipo para filtrar</span>}
              </label>
              <div className="local-busca-wrapper" ref={localBuscaRef}>
                <div className="input-with-icon">
                  <FiSearch />
                  <input
                    type="text"
                    className="local-busca-input"
                    placeholder={
                      formData.tipo_visita === 'mocidade' ? 'Buscar jovem pelo nome...' :
                      formData.tipo_visita === 'criancas' ? 'Buscar criança pelo nome...' :
                      'Buscar membro pelo nome...'
                    }
                    value={localBusca}
                    onChange={(e) => { setLocalBusca(e.target.value); setShowLocalSearch(true); }}
                    onFocus={() => setShowLocalSearch(true)}
                    autoComplete="off"
                  />
                  {localBusca && (
                    <button
                      type="button"
                      className="local-busca-limpar"
                      onClick={() => { setLocalBusca(''); setShowLocalSearch(false); }}
                    >
                      <FiX size={14} />
                    </button>
                  )}
                </div>
                {showLocalSearch && (localBusca || membrosLocalFiltrados.length > 0) && (
                  <div className="local-busca-dropdown">
                    {loadingMembros ? (
                      <div className="local-busca-loading">Carregando membros...</div>
                    ) : membrosLocalFiltrados.length === 0 ? (
                      <div className="local-busca-vazio">Nenhum membro encontrado</div>
                    ) : (
                      membrosLocalFiltrados.slice(0, 8).map(m => (
                        <button
                          key={m.id}
                          type="button"
                          className="local-busca-item"
                          onClick={() => selecionarMembroLocal(m)}
                        >
                          <span className={`local-busca-cat local-cat-${m.categoria}`}>
                            {m.categoria === 'crianca' ? '👶' : m.categoria === 'jovem' ? '🧒' : '👤'}
                          </span>
                          <span className="local-busca-nome">{getMemberName(m)}</span>
                          {m.endereco && (
                            <span className="local-busca-end">{m.endereco.replace(', Areiópolis - SP', '').substring(0, 40)}</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Local da Visita</label>
              <div className="input-with-icon">
                <FiMapPin />
                <input
                  type="text"
                  value={formData.local_visita}
                  onChange={(e) => handleInputChange('local_visita', e.target.value)}
                  placeholder="Nome do local"
                />
              </div>
            </div>
            
            <div className="form-group full-width">
              <label>Endereço Completo</label>
              <input
                type="text"
                value={formData.endereco}
                onChange={(e) => handleInputChange('endereco', e.target.value)}
                placeholder="Endereço completo do local"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Responsável</h2>
          
          <div className="form-group">
            <label>Responsável pela Visita</label>
            <div className="input-with-icon">
              <FiUser />
              <select
                value={formData.responsavel_id}
                onChange={(e) => handleInputChange('responsavel_id', e.target.value)}
              >
                <option value="">Selecione um responsável</option>
                {membrosDisponiveis.map(membro => (
                  <option key={membro.id} value={membro.id}>
                    {membro.nome} - {membro.categoria}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <h2>Participantes ({participantes.length})</h2>
            <button 
              type="button"
              onClick={() => setShowMembrosModal(true)}
              className="btn btn-primary btn-sm"
            >
              <FiPlus /> Adicionar Participante
            </button>
          </div>
          
          {participantes.length === 0 ? (
            <div className="empty-participants">
              <FiUsers size={32} />
              <p>Nenhum participante adicionado</p>
              <button 
                type="button"
                onClick={() => setShowMembrosModal(true)}
                className="btn btn-primary"
              >
                <FiPlus /> Adicionar Primeiro Participante
              </button>
            </div>
          ) : (
            <div className="participants-list">
              {participantes.map(participante => (
                <div key={participante.id} className="participant-item">
                  <div className="participant-info">
                    <div className="participant-name">{participante.nome}</div>
                    <div className="participant-category">{participante.categoria}</div>
                  </div>
                  
                  <div className="participant-actions">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={participante.presente || false}
                        onChange={() => togglePresenca(participante.id)}
                      />
                      Presente
                    </label>
                    
                    <button
                      type="button"
                      onClick={() => removerParticipante(participante.id)}
                      className="btn btn-danger btn-sm"
                    >
                      <FiX />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-section">
          <h2>Observações</h2>
          
          <div className="form-group">
            <label>Observações Adicionais</label>
            <textarea
              value={formData.observacoes}
              onChange={(e) => handleInputChange('observacoes', e.target.value)}
              placeholder="Digite observações sobre a visita..."
              rows={4}
            />
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            onClick={() => navigate('/visitas')} 
            className="btn btn-secondary"
          >
            Cancelar
          </button>
          
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="loading-spinner-sm"></div>
                {isEditing ? 'Atualizando...' : 'Criando...'}
              </>
            ) : (
              <>
                <FiSave />
                {isEditing ? 'Atualizar Visita' : 'Criar Visita'}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Modal de Seleção de Membros */}
      {showMembrosModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Adicionar Participante</h3>
              <button 
                onClick={() => setShowMembrosModal(false)}
                className="btn btn-secondary btn-sm"
              >
                <FiX />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="search-input">
                <FiSearch />
                <input
                  type="text"
                  placeholder="Buscar membro..."
                  value={buscarMembro}
                  onChange={(e) => setBuscarMembro(e.target.value)}
                />
              </div>
              
              {loadingMembros ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Carregando membros...</p>
                </div>
              ) : (
                <div className="membros-list">
                  {membrosFiltered.map(membro => (
                    <div 
                      key={membro.id} 
                      className={`membro-item ${
                        participantes.find(p => p.id === membro.id) ? 'already-added' : ''
                      }`}
                      onClick={() => !participantes.find(p => p.id === membro.id) && adicionarParticipante(membro)}
                    >
                      <div className="membro-info">
                        <div className="membro-name">{membro.nome}</div>
                        <div className="membro-category">{membro.categoria}</div>
                      </div>
                      
                      {participantes.find(p => p.id === membro.id) && (
                        <div className="already-added-badge">Já adicionado</div>
                      )}
                    </div>
                  ))}
                  
                  {membrosFiltered.length === 0 && (
                    <div className="empty-state">
                      <p>Nenhum membro encontrado</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitaForm;